import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import LegalPrivacy from "@/pages/LegalPrivacy";
import LegalCookies from "@/pages/LegalCookies";
import LegalTerms from "@/pages/LegalTerms";
import WhatCouldWeBuild from "@/pages/WhatCouldWeBuild";
import HospitalityConsulting from "@/pages/HospitalityConsulting";
import {
  CONSENT_STATUS,
  getConsent,
  onConsentChange,
} from "@/lib/consent";

const GA_ID = "G-R75P10QCRN";
let gaInitialised = false;
function ensureGa() {
  if (gaInitialised) return;
  ReactGA.initialize(GA_ID);
  gaInitialised = true;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // GA4 page view — only after consent.
    if (gaInitialised) {
      ReactGA.send({
        hitType: "pageview",
        page: pathname + hash,
        title: document.title,
      });
    }

    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  // GA: initialise / disable based on consent state.
  const [consent, setConsentState] = useState(() => getConsent());
  useEffect(() => {
    if (consent === CONSENT_STATUS.ACCEPTED) {
      ensureGa();
    } else {
      // Honour decline by disabling GA tracking globally.
      window[`ga-disable-${GA_ID}`] = true;
    }
  }, [consent]);
  useEffect(() => {
    return onConsentChange((e) => setConsentState(e.detail?.status || getConsent()));
  }, []);

  return (
    <div className="App relative">
      {!isAdmin && <Navbar />}
      <main>{children}</main>
      {!isAdmin && <Footer />}
      <StickyMobileCTA />
      {!isAdmin && <CookieBanner />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Shell>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />
          <Route path="/what-could-we-build" element={<WhatCouldWeBuild />} />
          <Route path="/hospitality-consulting" element={<HospitalityConsulting />} />
          <Route path="/privacy" element={<LegalPrivacy />} />
          <Route path="/cookies" element={<LegalCookies />} />
          <Route path="/terms" element={<LegalTerms />} />
          <Route path="/contact" element={<Navigate to="/#enquiry" replace />} />
          <Route path="/admin" element={<Admin />} />
          {/* Legacy routes — redirect to relevant home anchor */}
          <Route path="/services" element={<Navigate to="/#systems" replace />} />
          <Route path="/websites" element={<Navigate to="/#systems" replace />} />
          <Route path="/bespoke-apps" element={<Navigate to="/#systems" replace />} />
          <Route path="/google-visibility" element={<Navigate to="/#systems" replace />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </Shell>
    </BrowserRouter>
  );
}
